/* eslint-disable key-spacing */

import React from 'react';
import FormattedDate from '@/nike-tv/views/FormattedDate';
import {
    BestOfStatus,
    BreakStatus,
    CurrentPeriodStatus,
    FinishedStatus,
    NonStandardHalftimeStatus,
    RaindelayStatus,
    TieBreakStatus,
    TimeMMSSStatus,
    TimeMMStatus
} from './statuses';
import { activePeriod, periodScores } from '../services/eventDetail';
import Immutable from 'immutable';
import LiveMatchFacade from '../services/liveMatchFacade';
import i18n from '@i18n';
import moment from 'moment';
import { BetCount } from '@/nikeone/NikeOneCommon';
import { LiveMatchOptions } from '@/matchOptions/MatchOptionsButton';
import TicketStatus from './TicketStatus';
import { MatchPenaltyHome, MatchPenaltyAway } from '@/nikeone/components/live/MatchPenalty';
import clsx from 'clsx';
import { toSportType } from 'nike-ui/src/common/sports';

const InfoItem = function({ event, statuses, isFinished, clock }) {
    if (isFinished) {
        return null;
    }
    return (
        <span className="event-data">
            {statuses && statuses.map((Status, index) => <Status event={event} clock={clock} key={index} />)}
        </span>
    );
};

const CurrentPeriodItem = function({ event, style }) {
    const index = activePeriod(event);
    const scores = periodScores(event);

    const periodScoreHome = scores && scores.get(index).getIn(['score', 'home']);
    const periodScoreAway = scores && scores.get(index).getIn(['score', 'away']);

    if (!periodScoreHome) {
        return null;
    }
    return (
        <span className="event-period">
            <span className={'period-number' + (style ? ' ' + style : '')}>{periodScoreHome}</span>:
            <span className={'period-number' + (style ? ' ' + style : '')}>{periodScoreAway}</span>
        </span>
    );
};

const GameItem = function({ event, isFinished }) {
    if (isFinished) {
        return null;
    }
    const home = event.getIn(['score', 'game', 'home']);
    const away = event.getIn(['score', 'game', 'away']);
    return (
        <span className="event-game">
            <span className="sb-game">{home}</span>:<span className="sb-game">{away}</span>
        </span>
    );
};

const ServerIcon = function({ event, side }) {
    const server = event.get('server');
    return server === side ? <div className="service flex-shrink-0" /> : null;
};

const FinalScore = function({ event, side }) {
    const score = event.getIn(['score', 'total', side]);
    return score ? <span className="sb-final-score">{score === '-' ? 0 : score}</span> : null;
};

const NumberOfBettingEvents = function({ event, betCount }) {
    const numberOfBettingEvents = event ? event.get('numberOfBettingEvents') : betCount;
    return <BetCount betCount={numberOfBettingEvents} />;
};

const Streams = function({ event }) {
    const streamTypes = event.getIn(['streams'], Immutable.List()).map(stream => stream.get('streamType'));
    return streamTypes && streamTypes.includes('VIDEO') ? (
        <div className="icon-default-small icon-default-small-video-stream" />
    ) : null;
};

const RowView = function({
    children,
    event,
    clock,
    numberOfBettingEvents,
    showOptions,
    streams,
    pureLive,
    info,
    sportId,
    goToDetail
}) {
    const isFinished = LiveMatchFacade.isFinished(event.getIn(['eventStatus', 'code']));
    const homeOpponent = event.getIn(['opponents', 'home']);
    const awayOpponent = event.getIn(['opponents', 'away']);
    const eventId = event.get('id');
    const eventName = `${homeOpponent}${awayOpponent ? ` vs ${awayOpponent}` : ''}`;

    function onClick(e) {
        if (goToDetail) {
            goToDetail(e, eventName);
        }
    }

    const infos = React.Children.map(children, tab => {
        return React.cloneElement(tab, {
            event: event,
            clock: clock,
            isFinished: isFinished
        });
    });

    if (info) {
        return (
            <>
                {infos}
                {sportId === '7' ? <GameItem event={event} isFinished={isFinished} /> : null}
            </>
        );
    }

    return (
        <div className="nikeone-match-header" onClick={onClick} data-atid="nikeone-match-header">
            <div className="super flex items-center">
                <ServerIcon event={event} side="HOME" />
                <div className={'flex items-center home ' + (!awayOpponent ? 'home-only' : '')}>
                    <MatchPenaltyHome eventId={eventId} />
                    <span className="ellipsis">{homeOpponent}</span>
                </div>
                {awayOpponent && (
                    <>
                        <div className="separator live-bets">
                            <FinalScore event={event} side="home" />:<FinalScore event={event} side="away" />
                        </div>

                        <div className="flex items-center away">
                            <span className="ellipsis">{awayOpponent}</span>
                            <MatchPenaltyAway eventId={eventId} />
                        </div>
                    </>
                )}
                <ServerIcon event={event} side="AWAY" />
                <div className="flex-1 flex items-center justify-end">
                    {numberOfBettingEvents ? <NumberOfBettingEvents event={event} /> : null}
                    {showOptions ? <LiveMatchOptions eventId={event.get('id')} pureLive={pureLive} /> : null}
                </div>
            </div>
            <div className="score flex">
                {streams ? <Streams event={event} /> : null}
                {infos}
                <TicketStatus sportEventId={event.get('id')} />
            </div>
        </div>
    );
};

//{event, clock, numberOfBettingEvents, streams}
const DEFAULT_SCOREBOARD = function(params) {
    return (
        <RowView {...params}>
            <InfoItem
                statuses={[BreakStatus, NonStandardHalftimeStatus, CurrentPeriodStatus, TimeMMSSStatus, FinishedStatus]}
            />
        </RowView>
    );
};

const MINUTES_SCOREBOARD = function(params) {
    return (
        <RowView {...params}>
            <InfoItem
                statuses={[BreakStatus, NonStandardHalftimeStatus, CurrentPeriodStatus, TimeMMStatus, FinishedStatus]}
            />
        </RowView>
    );
};

const NO_TIME_SCOREBOARD = function(params) {
    return (
        <RowView {...params}>
            <InfoItem statuses={[BreakStatus, NonStandardHalftimeStatus, CurrentPeriodStatus, FinishedStatus]} />
        </RowView>
    );
};
const TENIS = function(params) {
    return (
        <RowView {...params}>
            <InfoItem statuses={[CurrentPeriodStatus, TieBreakStatus]} />
            <CurrentPeriodItem />
            <GameItem />
            <InfoItem statuses={[RaindelayStatus, FinishedStatus]} />
        </RowView>
    );
};
const SIPKY = function(params) {
    return (
        <RowView {...params}>
            <InfoItem
                statuses={[BreakStatus, NonStandardHalftimeStatus, CurrentPeriodStatus, BestOfStatus, FinishedStatus]}
            />
        </RowView>
    );
};

const layouts = {
    _default: DEFAULT_SCOREBOARD,
    futbal: MINUTES_SCOREBOARD,
    cyklistika: MINUTES_SCOREBOARD,
    futsal: MINUTES_SCOREBOARD,
    golf: MINUTES_SCOREBOARD,
    tenis: TENIS,
    sipky: SIPKY,
    stolnytenis: NO_TIME_SCOREBOARD,
    esports: NO_TIME_SCOREBOARD
};

const RowScoreboard = function({
    event,
    clock,
    sportId,
    numberOfBettingEvents,
    streams,
    showOptions,
    pureLive,
    goToDetail
}) {
    const sportName = toSportType(sportId);
    const Row = layouts[sportName] || layouts._default;
    return (
        <Row
            event={event}
            clock={clock}
            numberOfBettingEvents={numberOfBettingEvents}
            showOptions={showOptions}
            pureLive={pureLive}
            streams={streams}
            goToDetail={goToDetail}
        />
    );
};

export const PrematchScoreboard = ({ event }) => {
    const homeOpponent = event.getIn(['participants', 0]);
    const awayOpponent = event.getIn(['participants', 1]);
    const startDate = moment(event.get('expiration'));
    const betCount = event.get('betCount');

    return (
        <div className="nikeone-match-header">
            <div className="super flex items-center">
                <div className={clsx('home ellipsis', { 'home-only': !awayOpponent })}>{homeOpponent}</div>
                {awayOpponent && <div className="separator live-bets">{i18n('homeAway.vs.separator')}</div>}
                {awayOpponent && <div className="away ellipsis">{awayOpponent}</div>}
                {betCount && (
                    <div className="flex-1 flex items-center justify-end">
                        <NumberOfBettingEvents betCount={betCount} />
                    </div>
                )}
            </div>
            <div data-atid="date-expiration" className="event-data date-expiration">
                <FormattedDate startDate={startDate} />
            </div>
        </div>
    );
};

export default RowScoreboard;
